import { useState, useEffect } from "react";
import axios from "axios";
import {
  BookOpen, Plus, Loader2, Trash2, TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, Sparkles, Award, AlertCircle,
  Trophy, Target, RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v, digits = 2) => v == null || !Number.isFinite(Number(v))
  ? "—"
  : Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

const GRADE_COLOR = {
  A: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  B: "bg-teal-500/20 text-teal-300 border-teal-500/40",
  C: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  D: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  F: "bg-red-500/20 text-red-300 border-red-500/40",
};

export default function TradeJournal() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const { data } = await axios.get(`${API}/journal/entries`);
      setEntries(data.entries || []);
      setStats(data.stats || {});
    } catch (e) {
      toast.error("Failed to load journal");
    } finally {
      setLoading(false);
    }
  };

  const reviewEntry = async (id) => {
    setReviewingId(id);
    try {
      const { data } = await axios.post(`${API}/journal/entries/${id}/review`, {}, { timeout: 90000 });
      setEntries(prev => prev.map(e => e.id === id ? data : e));
      toast.success(`AI grade: ${data.review?.grade || "-"}`);
      // Refetch to update aggregate stats (grade distribution etc.)
      fetchEntries();
    } catch (e) {
      toast.error("AI review failed - try again");
    } finally {
      setReviewingId(null);
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this journal entry?")) return;
    try {
      await axios.delete(`${API}/journal/entries/${id}`);
      toast.success("Entry deleted");
      fetchEntries();
    } catch (e) { toast.error("Delete failed"); }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="journal-loading">
        <Skeleton className="h-8 w-72" />
        <div className="grid gap-4 md:grid-cols-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-slide-up" data-testid="trade-journal-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase flex items-center gap-2" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            <BookOpen className="w-7 h-7 text-amber-500" />
            Trade Journal
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Log closed trades and let AI grade your entry against the AuraTrade confluence framework
          </p>
        </div>
        <AddEntryDialog open={addOpen} setOpen={setAddOpen} onDone={fetchEntries} />
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Total Trades" value={stats.total || 0} icon={BookOpen} testid="stat-total" />
        <StatCard label="Win Rate" value={`${fmt(stats.win_rate, 1)}%`} icon={Trophy} testid="stat-winrate" />
        <StatCard label="Avg R Multiple" value={fmt(stats.avg_r_multiple, 2)} icon={Target} testid="stat-r" />
        <StatCard
          label="Grade Distribution"
          value={
            Object.keys(stats.grade_distribution || {}).length > 0 ? (
              <div className="flex gap-1.5 text-xs">
                {["A", "B", "C", "D", "F"].map(g => (
                  (stats.grade_distribution || {})[g] ? (
                    <span key={g} className={`${GRADE_COLOR[g]} px-1.5 py-0.5 rounded border`}>
                      {g}·{stats.grade_distribution[g]}
                    </span>
                  ) : null
                ))}
              </div>
            ) : "—"
          }
          icon={Award}
          testid="stat-grades"
        />
      </div>

      {/* Entries */}
      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 flex flex-col items-center gap-3 text-center">
            <BookOpen className="w-10 h-10 text-muted-foreground" />
            <div className="font-medium">No journal entries yet</div>
            <p className="text-sm text-muted-foreground max-w-md">
              Log your first closed AuraTrade setup to get an AI-graded review against confluence, discipline, and emotion.
            </p>
            <Button onClick={() => setAddOpen(true)} data-testid="log-first-trade-btn">
              <Plus className="w-4 h-4 mr-2" /> Log Your First Trade
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map(entry => (
            <EntryCard
              key={entry.id}
              entry={entry}
              reviewing={reviewingId === entry.id}
              onReview={() => reviewEntry(entry.id)}
              onDelete={() => deleteEntry(entry.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="pt-6">
        <div className="flex justify-between items-start mb-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
          {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
        </div>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

function EntryCard({ entry, reviewing, onReview, onDelete }) {
  const review = entry.review;
  const pnlPos = (entry.pnl || 0) >= 0;
  const zone = entry.zone_snapshot;

  return (
    <Card data-testid={`journal-entry-${entry.id}`} className={review?.grade ? `border-l-4` : ""} style={review?.grade ? { borderLeftColor: gradeColorHex(review.grade) } : {}}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={entry.direction === "LONG" ? "text-emerald-400 border-emerald-500/40" : "text-red-400 border-red-500/40"}>
                {entry.direction === "LONG" ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
                {entry.direction}
              </Badge>
              <span className="font-semibold">{entry.instrument_label || entry.symbol}</span>
              {zone && (
                <Badge className="text-[10px] bg-purple-500/20 text-purple-300 border-purple-500/40">
                  <Sparkles className="w-3 h-3 mr-1" /> Linked to zone ({zone.conviction_pct}%)
                </Badge>
              )}
              {review?.grade && (
                <Badge className={`${GRADE_COLOR[review.grade]} font-bold`}>
                  Grade {review.grade}
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-3 text-xs">
              <Kv k="Entry" v={fmt(entry.entry_price, 4)} />
              <Kv k="Exit" v={fmt(entry.exit_price, 4)} />
              <Kv k="P&L" v={`${pnlPos ? "+" : ""}${fmt(entry.pnl, 2)}`} className={pnlPos ? "text-emerald-400" : "text-red-400"} />
              <Kv k="P&L %" v={`${pnlPos ? "+" : ""}${fmt(entry.pnl_pct, 2)}%`} className={pnlPos ? "text-emerald-400" : "text-red-400"} />
              <Kv k="R Multiple" v={entry.r_multiple != null ? `${entry.r_multiple}R` : "—"} />
            </div>
            {entry.notes && (
              <div className="mt-3 text-xs text-muted-foreground italic border-l-2 border-slate-700 pl-3">
                "{entry.notes}"
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Button size="sm" onClick={onReview} disabled={reviewing} data-testid={`review-btn-${entry.id}`}>
              {reviewing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
              {review ? "Re-review" : "AI Review"}
            </Button>
            <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-400 hover:bg-red-500/10" data-testid={`delete-entry-btn-${entry.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* AI Review */}
        {review && (
          <div className="mt-4 p-4 rounded-lg bg-slate-800/40 border border-slate-700/40 space-y-3">
            <div className="grid grid-cols-3 gap-3 text-xs">
              <GradeBadge label="Confluence" grade={review.confluence_grade} />
              <GradeBadge label="Discipline" grade={review.discipline_grade} />
              <GradeBadge label="Emotion / Plan" grade={review.emotion_grade} />
            </div>
            <p className="text-sm">{review.summary}</p>
            {review.key_learning && (
              <div className="text-xs bg-amber-500/10 border border-amber-500/20 rounded p-2.5">
                <span className="font-medium text-amber-400">Key Learning:</span> {review.key_learning}
              </div>
            )}
            {(review.strengths?.length || review.weaknesses?.length) && (
              <div className="grid grid-cols-2 gap-3 text-xs">
                {review.strengths?.length > 0 && (
                  <div>
                    <div className="text-emerald-400 font-medium mb-1">Strengths</div>
                    <ul className="text-muted-foreground space-y-0.5 list-disc list-inside">
                      {review.strengths.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {review.weaknesses?.length > 0 && (
                  <div>
                    <div className="text-red-400 font-medium mb-1">Weaknesses</div>
                    <ul className="text-muted-foreground space-y-0.5 list-disc list-inside">
                      {review.weaknesses.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="text-[10px] text-muted-foreground pt-1 border-t border-slate-700/40">
              Reviewed by {review.model || "AI"} · {review.generated_at ? new Date(review.generated_at).toLocaleString() : ""}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function gradeColorHex(g) {
  return { A: "#10b981", B: "#14b8a6", C: "#f59e0b", D: "#f97316", F: "#ef4444" }[g] || "#64748b";
}

function GradeBadge({ label, grade }) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <div className={`inline-block px-2.5 py-1 rounded border font-bold ${GRADE_COLOR[grade] || "bg-slate-500/20 text-slate-400 border-slate-500/40"}`}>
        {grade || "—"}
      </div>
    </div>
  );
}

function Kv({ k, v, className = "" }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-muted-foreground">{k}</div>
      <div className={`font-medium ${className}`}>{v}</div>
    </div>
  );
}

function AddEntryDialog({ open, setOpen, onDone }) {
  const [form, setForm] = useState({
    symbol: "", instrument_label: "", direction: "LONG",
    entry_price: "", exit_price: "", position_size: 1,
    planned_stop: "", planned_target: "", notes: "",
  });
  const [linkedZone, setLinkedZone] = useState(null);
  const [linking, setLinking] = useState(false);
  const [busy, setBusy] = useState(false);

  const linkToZone = async () => {
    if (!form.symbol) { toast.error("Enter a symbol first"); return; }
    setLinking(true);
    try {
      const { data } = await axios.get(`${API}/aura/intraday/zones/${form.symbol}`);
      const topZone = data.top_zone;
      if (topZone) {
        const snapshot = {
          conviction_pct: topZone.conviction.score_pct,
          tier: topZone.conviction.tier,
          band_low: topZone.band_low,
          band_high: topZone.band_high,
          factors: topZone.factors.map(f => f.name),
        };
        setLinkedZone(snapshot);
        // Pre-fill planned levels from trade recommendation if empty
        if (data.recommended_trade) {
          const rt = data.recommended_trade;
          setForm(f => ({
            ...f,
            instrument_label: data.label,
            direction: rt.direction || f.direction,
            planned_stop: f.planned_stop || rt.stop,
            planned_target: f.planned_target || rt.target,
          }));
        }
        toast.success(`Linked to ${data.label} zone (${topZone.conviction.score_pct}%)`);
      } else {
        toast.error("No zone found for that symbol");
      }
    } catch (e) {
      toast.error("Failed to fetch zone. Check symbol.");
    } finally {
      setLinking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...form,
        entry_price: parseFloat(form.entry_price),
        exit_price: parseFloat(form.exit_price),
        position_size: parseFloat(form.position_size) || 1,
        planned_stop: form.planned_stop === "" ? null : parseFloat(form.planned_stop),
        planned_target: form.planned_target === "" ? null : parseFloat(form.planned_target),
        zone_snapshot: linkedZone,
        closed_at: new Date().toISOString(),
      };
      await axios.post(`${API}/journal/entries`, body);
      toast.success("Trade logged");
      setOpen(false);
      setForm({ symbol: "", instrument_label: "", direction: "LONG", entry_price: "", exit_price: "", position_size: 1, planned_stop: "", planned_target: "", notes: "" });
      setLinkedZone(null);
      onDone();
    } catch (e) {
      toast.error("Failed to log trade");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="log-trade-btn"><Plus className="w-4 h-4 mr-2" /> Log Trade</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Log Closed Trade</DialogTitle>
          <DialogDescription>Optionally link to a current AuraTrade zone for the AI review to grade your entry.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label>Symbol</Label>
              <Input required value={form.symbol} onChange={e => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
                placeholder="ES=F, EURUSD=X, GC=F..." data-testid="journal-symbol-input" />
            </div>
            <div className="flex flex-col justify-end">
              <Button type="button" variant="outline" onClick={linkToZone} disabled={linking}
                data-testid="link-zone-btn" title="Fetch current AuraTrade zone">
                {linking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
          {linkedZone && (
            <div className="text-xs bg-purple-500/10 border border-purple-500/30 rounded p-2">
              <span className="text-purple-300">✓ Linked zone:</span> {linkedZone.conviction_pct}% ({linkedZone.tier}) · band {fmt(linkedZone.band_low, 4)}-{fmt(linkedZone.band_high, 4)}
            </div>
          )}
          <div>
            <Label>Direction</Label>
            <Select value={form.direction} onValueChange={v => setForm({ ...form, direction: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="LONG">LONG</SelectItem>
                <SelectItem value="SHORT">SHORT</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Entry</Label><Input type="number" step="0.0001" required value={form.entry_price} onChange={e => setForm({ ...form, entry_price: e.target.value })} data-testid="entry-price-input" /></div>
            <div><Label>Exit</Label><Input type="number" step="0.0001" required value={form.exit_price} onChange={e => setForm({ ...form, exit_price: e.target.value })} data-testid="exit-price-input" /></div>
            <div><Label>Size</Label><Input type="number" step="0.01" value={form.position_size} onChange={e => setForm({ ...form, position_size: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Planned Stop</Label><Input type="number" step="0.0001" value={form.planned_stop} onChange={e => setForm({ ...form, planned_stop: e.target.value })} /></div>
            <div><Label>Planned Target</Label><Input type="number" step="0.0001" value={form.planned_target} onChange={e => setForm({ ...form, planned_target: e.target.value })} /></div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="What was the plan? How did the trade go?" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="submit-journal-entry">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Log Trade
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
